import { api, API_BASE_URL } from '../../lib/api';
import { fmtDate } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

/**
 * Task — tipi, query options e helper condivisi tra TasksPage, TaskDetailPage,
 * NewTaskModal, Sidebar (badge) e lo stream "Oggi".
 *
 * API: /api/v1/tasks (apps/api/src/modules/tasks). In UI un task è "Da fare"
 * oppure "Completato": gli altri stati (IN_PROGRESS, REVIEW, BLOCKED) restano
 * nel modello per la kanban di progetto ma qui si trattano come "aperti".
 * Commenti, checklist e log esistono nell'API ma NON si mostrano (scelta di
 * Lorenzo: meno dati per task). Query keys: prefisso ['tasks', ...].
 */

export type TaskScope = 'mine' | 'assigned-by-me' | 'all';
export type TaskStatusKey = 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'BLOCKED' | 'DONE';
export type TaskPriorityKey = 'BASSA' | 'MEDIA' | 'ALTA';
export type TaskRecurrenceKey = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
export type TaskDueFilter = '' | 'today' | 'overdue' | 'week' | 'none';

export interface TaskUserLite {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  avatarColor?: string | null;
}

export interface TaskItem {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatusKey;
  priority: TaskPriorityKey;
  dueDate: string | null;
  startDate: string | null;
  remindAt: string | null;
  recurrence: TaskRecurrenceKey;
  isPrivate: boolean;
  completedAt: string | null;
  order: number;
  tag: string | null;
  assignee: TaskUserLite | null;
  createdBy?: TaskUserLite | null;
  client: { id: string; shortName: string; legalName?: string } | null;
  project: { id: string; code: string; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Dettaglio: stessi campi della riga + canManage (commenti/checklist/log arrivano ma non si usano). */
export type TaskFull = TaskItem & {
  /** false se chi guarda non è creatore, assegnatario o admin TASKS: controlli in sola lettura. */
  canManage?: boolean;
};

export interface TaskListResponse {
  items: TaskItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Voce del riepilogo: campi minimi (l'API non include cliente/progetto qui). */
export type TaskSummaryItem = Pick<TaskItem, 'id' | 'title' | 'dueDate' | 'priority' | 'status' | 'assignee'>
  & Partial<Pick<TaskItem, 'client' | 'project'>>;

export interface TaskSummary {
  scope?: TaskScope;
  /** YYYY-MM-DD di "oggi" secondo il server (Europe/Rome). */
  today?: string;
  todo: number;
  dueToday: number;
  overdue: number;
  doneThisWeek: number;
  byStatus?: Partial<Record<TaskStatusKey, number>>;
  dueTodayItems: TaskSummaryItem[];
  overdueItems: TaskSummaryItem[];
}

export interface TaskListParams {
  scope: TaskScope;
  status?: string;
  assigneeId?: string;
  clientId?: string;
  projectId?: string;
  search?: string;
  due?: TaskDueFilter;
  includeDone?: boolean;
  /** Ordina per (solo la lista): scadenza, priorita, assegnato, recenti, titolo, completati. */
  sort?: string;
  /** Alta oppure Normale (MEDIA e BASSA del modello): lo filtra l'API su tutta la lista. */
  priorita?: 'ALTA' | 'NORMALE';
  page?: number;
  pageSize?: number;
}

// ── Etichette (copy italiano, parole che si usano davvero) ──────────────────

/** In UI due soli stati: aperto (tutto ciò che non è DONE) e completato. */
export const isDone = (status: TaskStatusKey | string) => status === 'DONE';

/** Priorità semplice: Alta oppure Normale (MEDIA e BASSA del modello). */
export const isHigh = (p: TaskPriorityKey | string) => p === 'ALTA';
export const PRIORITY_LABEL: Record<TaskPriorityKey, string> = { BASSA: 'Normale', MEDIA: 'Normale', ALTA: 'Alta' };

export const RECURRENCES: TaskRecurrenceKey[] = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
export const RECURRENCE_LABEL: Record<TaskRecurrenceKey, string> = {
  NONE: 'Nessuna',
  DAILY: 'Ogni giorno',
  WEEKLY: 'Ogni settimana',
  MONTHLY: 'Ogni mese',
  YEARLY: 'Ogni anno',
};

export const SCOPES: TaskScope[] = ['mine', 'assigned-by-me', 'all'];
export const SCOPE_LABEL: Record<TaskScope, string> = {
  mine: 'Miei',
  'assigned-by-me': 'Assegnati da me',
  all: 'Tutti',
};

// ── Query options (cache condivisa tra le viste) ────────────────────────────

export function tasksSummaryQuery(scope: TaskScope = 'mine') {
  return {
    queryKey: ['tasks', 'summary', scope] as const,
    queryFn: () => api.get<TaskSummary>(`/tasks/summary?scope=${scope}`),
    // Il riepilogo alimenta badge Sidebar, Topbar e stream Oggi da ogni pagina:
    // un fetch al minuto basta, niente raffiche a ogni navigazione.
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

export const assignableUsersQuery = {
  queryKey: ['tasks', 'assignable-users'] as const,
  // L'API risponde { items: [...] } (il contratto diceva array nudo): si accettano entrambi
  queryFn: async (): Promise<TaskUserLite[]> => {
    const r = await api.get<TaskUserLite[] | { items: TaskUserLite[] }>('/tasks/assignable-users');
    return Array.isArray(r) ? r : (r?.items ?? []);
  },
  staleTime: 5 * 60_000,
};

export function buildListQuery(p: TaskListParams): string {
  const sp = new URLSearchParams();
  sp.set('scope', p.scope);
  if (p.status) sp.set('status', p.status);
  if (p.assigneeId) sp.set('assigneeId', p.assigneeId);
  if (p.clientId) sp.set('clientId', p.clientId);
  if (p.projectId) sp.set('projectId', p.projectId);
  if (p.search?.trim()) sp.set('search', p.search.trim());
  if (p.due) sp.set('due', p.due);
  if (p.includeDone != null) sp.set('includeDone', String(p.includeDone));
  if (p.sort) sp.set('sort', p.sort);
  if (p.priorita) sp.set('priorita', p.priorita);
  sp.set('page', String(p.page ?? 1));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return `/tasks?${sp.toString()}`;
}

export function tasksListQuery(p: TaskListParams) {
  return {
    queryKey: ['tasks', 'list', p] as const,
    queryFn: () => api.get<TaskListResponse>(buildListQuery(p)),
    staleTime: 15_000,
  };
}

export function taskDetailQuery(id: string) {
  return {
    queryKey: ['tasks', 'detail', id] as const,
    queryFn: () => api.get<TaskFull>(`/tasks/${id}`),
  };
}

// Picker cliente: il CRM è in pausa in UI ma l'API resta viva.
export const clientsLiteQuery = {
  queryKey: ['tasks', 'clients-lite'] as const,
  queryFn: () => api.get<{ items: Array<{ id: string; shortName: string; legalName: string }> }>('/clients?pageSize=200'),
  staleTime: 5 * 60_000,
};

// ── Date: scadenze "oggi / domani / in ritardo di N g" ──────────────────────

const DAY_MS = 86400000;

/**
 * Chiave giorno (YYYY-MM-DD) di una data di scadenza. Le dueDate arrivano a
 * mezzanotte UTC (date-only): in quel caso si leggono i componenti UTC, così
 * il 19/09 resta il 19/09 anche visto dall'Italia. Per gli orari veri
 * (promemoria) valgono i componenti locali.
 */
export function dayKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const utcMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  if (utcMidnight) return d.toISOString().slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayKey(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Giorni tra oggi e la scadenza (negativo = in ritardo), null senza data. */
export function daysToDue(iso: string | null | undefined): number | null {
  const k = dayKey(iso);
  if (!k) return null;
  const [y, m, d] = k.split('-').map(Number);
  const [ty, tm, td] = todayKey().split('-').map(Number);
  return Math.round((Date.UTC(y!, m! - 1, d!) - Date.UTC(ty!, tm! - 1, td!)) / DAY_MS);
}

export interface DueInfo { label: string; tone: 'alert' | 'warn' | 'mute'; days: number | null }

/** Etichetta scadenza per la lista: "oggi", "domani", "in ritardo di 3 g", altrimenti la data. */
export function dueInfo(iso: string | null | undefined, done = false): DueInfo {
  const days = daysToDue(iso);
  if (days == null) return { label: '—', tone: 'mute', days: null };
  if (done) return { label: fmtDate(iso), tone: 'mute', days };
  if (days < 0) return { label: `in ritardo di ${-days} g`, tone: 'alert', days };
  if (days === 0) return { label: 'oggi', tone: 'warn', days };
  if (days === 1) return { label: 'domani', tone: 'warn', days };
  if (days <= 6) return { label: `tra ${days} g`, tone: 'mute', days };
  return { label: fmtDate(iso), tone: 'mute', days };
}

/** Data della prossima occorrenza (per il toast "Completa" di un task ricorrente). */
export function nextOccurrence(dueDate: string | null | undefined, recurrence: TaskRecurrenceKey): Date | null {
  const k = dayKey(dueDate);
  if (!k || recurrence === 'NONE') return null;
  const [y, m, d] = k.split('-').map(Number);
  const next = new Date(Date.UTC(y!, m! - 1, d!));
  if (recurrence === 'DAILY') next.setUTCDate(next.getUTCDate() + 1);
  else if (recurrence === 'WEEKLY') next.setUTCDate(next.getUTCDate() + 7);
  else if (recurrence === 'MONTHLY') next.setUTCMonth(next.getUTCMonth() + 1);
  else next.setUTCFullYear(next.getUTCFullYear() + 1);
  return next;
}

/** Valore per <input type="date"> da una ISO (date-only o datetime). */
export function toDateInput(iso: string | null | undefined): string {
  return dayKey(iso) ?? '';
}

/** Valore per <input type="datetime-local"> da una ISO (ora locale). */
export function toDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Da datetime-local (ora locale) a ISO UTC per l'API. */
export function fromDateTimeInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** "17/09/2026 09:00" per un promemoria. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${fmtDate(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Persone ────────────────────────────────────────────────────────────────

export function shortName(u: TaskUserLite | null | undefined): string {
  if (!u) return '—';
  return `${u.firstName.charAt(0)}. ${u.lastName}`;
}

export function fullName(u: TaskUserLite | null | undefined): string {
  if (!u) return '—';
  return `${u.firstName} ${u.lastName}`.trim();
}

/** "Cliente · PRJ-01" sotto il titolo, senza ripetere nulla se manca. */
export function contextLine(t: Partial<Pick<TaskItem, 'client' | 'project'>>): string {
  const parts: string[] = [];
  if (t.client?.shortName) parts.push(t.client.shortName);
  if (t.project?.code) parts.push(t.project.code);
  return parts.join(' · ');
}

// ── Calendario: link Google + download .ics ────────────────────────────────

function pad2(n: number) { return String(n).padStart(2, '0'); }

/** Link "Aggiungi a Google Calendar" costruito lato client (nessun backend). */
export function googleCalendarUrl(t: Pick<TaskItem, 'id' | 'title' | 'description' | 'dueDate' | 'remindAt'>): string | null {
  let dates: string;
  if (t.remindAt) {
    // Promemoria con orario → evento di un'ora, in UTC (suffisso Z)
    const start = new Date(t.remindAt);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 60 * 60_000);
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    dates = `${fmt(start)}/${fmt(end)}`;
  } else if (t.dueDate) {
    // Solo data → evento "tutto il giorno" (la fine è esclusiva: giorno dopo)
    const k = dayKey(t.dueDate);
    if (!k) return null;
    const [y, m, d] = k.split('-').map(Number);
    const next = new Date(Date.UTC(y!, m! - 1, d! + 1));
    const a = k.replace(/-/g, '');
    const b = `${next.getUTCFullYear()}${pad2(next.getUTCMonth() + 1)}${pad2(next.getUTCDate())}`;
    dates = `${a}/${b}`;
  } else {
    return null;
  }
  const details = [t.description?.trim(), `${window.location.origin}/tasks/${t.id}`].filter(Boolean).join('\n\n');
  const sp = new URLSearchParams({ action: 'TEMPLATE', text: t.title, dates, details });
  return `https://calendar.google.com/calendar/render?${sp.toString()}`;
}

/** Scarica /tasks/:id/event.ics con il Bearer (fetch → blob → <a download>). */
export async function downloadTaskIcs(id: string, title: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/tasks/${id}/event.ics`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Download non riuscito');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = title.replace(/[^\w\-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'task';
  a.href = url; a.download = `${safe}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
